import React, { useState, useRef } from 'react';
import { ArrowLeft, User, Mail, Lock, Shield, Key, Camera, Trash2, CheckCircle, AlertCircle, Upload, X, Monitor, Smartphone, Globe } from 'lucide-react';

function AccountPage({ onBack }) {
  const fileInputRef = useRef(null);
  const [user, setUser] = useState({
    name: 'John Doe',
    email: 'john.doe@example.com',
    profilePicture: null,
    authProvider: 'local', // 'local', 'google', 'linkedin'
    lastLoginMethod: null // For display purposes
  });
  const [password, setPassword] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [security, setSecurity] = useState({
    twoFactorEnabled: false,
    activeSessions: 3,
    lastLogin: new Date(Date.now() - 3600000) // 1 hour ago
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    // Simulate API call
    setTimeout(() => {
      setSaving(false);
      setMessage({ type: 'success', text: 'Profile updated successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }, 1000);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    if (password.new !== password.confirm) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      setSaving(false);
      return;
    }

    if (password.new.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      setSaving(false);
      return;
    }

    // Simulate API call
    setTimeout(() => {
      setSaving(false);
      setMessage({ type: 'success', text: 'Password changed successfully' });
      setPassword({ current: '', new: '', confirm: '' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }, 1000);
  };

  const handleTwoFactorToggle = () => {
    setSecurity({
      ...security,
      twoFactorEnabled: !security.twoFactorEnabled
    });
    setMessage({
      type: 'success',
      text: `Two-factor authentication ${!security.twoFactorEnabled ? 'enabled' : 'disabled'}`
    });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setMessage({ type: 'error', text: 'Image must be less than 5MB' });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setUser({ ...user, profilePicture: reader.result });
        setMessage({ type: 'success', text: 'Profile picture updated' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setUser({ ...user, profilePicture: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setMessage({ type: 'success', text: 'Profile picture removed' });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleDeleteAccount = () => {
    // Close modal
    setShowDeleteModal(false);

    // Simulate API call
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setMessage({ type: 'success', text: 'Account deletion initiated. You will receive a confirmation email.' });
      // In real app: navigate to login or show confirmation
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Dashboard</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl shadow-lg">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 font-heading">Account Settings</h1>
              <p className="text-slate-600">Manage your profile and preferences</p>
            </div>
          </div>
        </div>

        {/* Message Banner */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${message.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
            <span className="text-sm font-medium">{message.text}</span>
          </div>
        )}

        {/* Profile Information */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="text-xl font-bold text-slate-900 font-heading mb-6 flex items-center gap-2">
            <User className="w-5 h-5" />
            Profile Information
          </h2>

          <form onSubmit={handleProfileUpdate}>
            <div className="space-y-6">
              {/* Profile Picture */}
              <div className="flex items-center gap-6 pb-6 border-b border-slate-200">
                <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                  <div className="w-24 h-24 bg-gradient-to-br from-slate-600 to-slate-700 rounded-full flex items-center justify-center text-white font-semibold text-2xl shadow-lg overflow-hidden transition-all group-hover:ring-4 group-hover:ring-primary-500/50">
                    {user.profilePicture ? (
                      <img src={user.profilePicture} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      user.name.split(' ').map(n => n[0]).join('')
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                  <div className="absolute bottom-0 right-0 p-2.5 bg-primary-600 rounded-full text-white shadow-lg group-hover:bg-primary-700 transition-all group-hover:scale-110 pointer-events-none">
                    <Camera className="w-4 h-4" />
                  </div>
                  <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-3 py-1 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                    Click to upload
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-900 mb-1">Profile Picture</div>
                  <div className="text-xs text-slate-500 mb-3">Click photo to upload • JPG, PNG or GIF • Max 5MB</div>
                  {user.profilePicture && (
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="px-4 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Remove Photo
                    </button>
                  )}
                </div>
              </div>

              {/* Name & Email - 2 Columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={user.name}
                    onChange={(e) => setUser({ ...user, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={user.email}
                    onChange={(e) => setUser({ ...user, email: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
                    required
                  />
                  <p className="mt-1.5 text-xs text-slate-500">Your email will never be shared publicly</p>
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end pt-4 border-t border-slate-200">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2.5 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* 2-Column Layout for Password & Security */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Password / SSO Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 font-heading mb-6 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              {user.authProvider === 'local' ? 'Change Password' : 'Sign-In Method'}
            </h2>

            {user.authProvider === 'local' ? (
              <form onSubmit={handlePasswordChange}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-900 mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={password.current}
                      onChange={(e) => setPassword({ ...password, current: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-900 mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={password.new}
                      onChange={(e) => setPassword({ ...password, new: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
                      required
                      minLength={8}
                    />
                    <p className="mt-1.5 text-xs text-slate-500">Must be at least 8 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-900 mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={password.confirm}
                      onChange={(e) => setPassword({ ...password, confirm: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
                      required
                    />
                  </div>

                  <div className="pt-4 border-t border-slate-200">
                    <button
                      type="submit"
                      disabled={saving}
                      className="w-full px-6 py-2.5 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {saving ? 'Updating...' : 'Update Password'}
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${user.authProvider === 'google'
                        ? 'bg-white border-2 border-blue-500'
                        : 'bg-blue-600'
                      }`}>
                      {user.authProvider === 'google' ? (
                        <svg className="w-6 h-6" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-slate-900 mb-1">
                        Signed in with {user.authProvider === 'google' ? 'Google' : 'LinkedIn'}
                      </div>
                      <div className="text-xs text-slate-600 mb-3">
                        You're using {user.authProvider === 'google' ? 'Google' : 'LinkedIn'} to sign in. Your password is managed by {user.authProvider === 'google' ? 'Google' : 'LinkedIn'}.
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Secure authentication</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <div className="text-xs font-semibold text-slate-700 uppercase mb-2">Account Security</div>
                  <div className="text-sm text-slate-600">
                    To change your password or security settings, please visit your {user.authProvider === 'google' ? 'Google' : 'LinkedIn'} account settings.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Security Settings */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 font-heading mb-6 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Security
            </h2>

            <div className="space-y-4">
              {/* Two-Factor Authentication */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 rounded-lg">
                    <Key className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-900">Two-Factor Auth</div>
                    <div className="text-xs text-slate-500">Add extra security layer</div>
                  </div>
                </div>
                <button
                  onClick={handleTwoFactorToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${security.twoFactorEnabled ? 'bg-primary-600' : 'bg-slate-300'
                    }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${security.twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                  />
                </button>
              </div>

              {/* Active Sessions */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Shield className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-900">Active Sessions</div>
                    <div className="text-xs text-slate-500">{security.activeSessions} active sessions</div>
                  </div>
                </div>
                <div className="space-y-2 mb-3">
                  <div className="flex items-center gap-2 text-xs text-slate-600 pl-11">
                    <Monitor className="w-3 h-3" />
                    <span>Chrome on macOS - Current</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 pl-11">
                    <Smartphone className="w-3 h-3" />
                    <span>iPhone - 2 hours ago</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 pl-11">
                    <Globe className="w-3 h-3" />
                    <span>Firefox on Windows - 1 day ago</span>
                  </div>
                </div>
                <div className="text-xs text-slate-600 mb-2 pl-11">
                  Last login: {security.lastLogin.toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                <button
                  onClick={() => setShowSessionsModal(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium pl-11 transition-colors"
                >
                  View all sessions →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-6">
          <h2 className="text-xl font-bold text-red-600 font-heading mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Danger Zone
          </h2>

          <div className="p-4 bg-red-50 rounded-xl border border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-red-900 mb-1">Delete Account</div>
                <div className="text-xs text-red-700">
                  Permanently delete your account and all data. This action cannot be undone.
                </div>
              </div>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2 text-sm"
              >
                <Trash2 className="w-4 h-4" />
                Delete Account
              </button>
            </div>
          </div>
        </div>

        {/* Sessions Modal */}
        {showSessionsModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowSessionsModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-900">Active Sessions</h3>
                <button onClick={() => setShowSessionsModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-3 mb-6">
                {/* Session 1 - Current */}
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Monitor className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <div className="font-medium text-slate-900 text-sm">Chrome on macOS</div>
                        <div className="text-xs text-slate-600 mt-1">IP: 192.168.1.100</div>
                        <div className="text-xs text-slate-500">San Francisco, CA • Active now</div>
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-green-600 text-white text-xs font-medium rounded">Current</span>
                  </div>
                </div>

                {/* Session 2 */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Smartphone className="w-5 h-5 text-slate-600 mt-0.5" />
                      <div>
                        <div className="font-medium text-slate-900 text-sm">Safari on iPhone</div>
                        <div className="text-xs text-slate-600 mt-1">IP: 10.0.0.45</div>
                        <div className="text-xs text-slate-500">New York, NY • 2 hours ago</div>
                      </div>
                    </div>
                    <button className="text-xs text-red-600 hover:text-red-700 font-medium">Revoke</button>
                  </div>
                </div>

                {/* Session 3 */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Globe className="w-5 h-5 text-slate-600 mt-0.5" />
                      <div>
                        <div className="font-medium text-slate-900 text-sm">Firefox on Windows</div>
                        <div className="text-xs text-slate-600 mt-1">IP: 176.32.98.166</div>
                        <div className="text-xs text-slate-500">London, UK • 1 day ago</div>
                      </div>
                    </div>
                    <button className="text-xs text-red-600 hover:text-red-700 font-medium">Revoke</button>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowSessionsModal(false)}
                className="w-full px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Delete Account Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">Delete Account?</h3>
              </div>

              <p className="text-slate-600 mb-6">
                Are you absolutely sure? This action <strong>cannot be undone</strong>. All your data, including:
              </p>

              <ul className="space-y-2 mb-6 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                  All property analyses and reports
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                  Saved searches and preferences
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                  Account history and settings
                </li>
              </ul>

              <p className="text-sm text-slate-500 mb-6 p-3 bg-slate-50 rounded-lg border border-slate-200">
                💡 <strong>Tip:</strong> You can download your data before deletion by contacting support.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AccountPage;
